"use client";

import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getAddressAttributes } from "@/lib/utils";
import { useCheckoutStore } from "@/stores/checkout-store";
import { Loader2 } from "lucide-react";

type BillingRecapProps = {
  locale: Locales;
};

const BillingRecap = ({ locale }: BillingRecapProps) => {
  const { customer } = useCustomer();
  const billingAddressId = useCheckoutStore((state) => state.billingAddress);
  const { billingAddress, billingAddressMetadata } = getAddressAttributes(customer?.addresses, billingAddressId);

  const fields = [
    billingAddress?.company,
    billingAddressMetadata.contact_person,
    billingAddressMetadata.vat_number,
    billingAddress?.address_1,
    billingAddress?.postal_code,
    billingAddress?.city,
  ];

  const hasBillingInfo = fields.some(Boolean);

  if (customer !== undefined && !hasBillingInfo) {
    return null;
  }

  return (
    <section className="space-y-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Billing", locale)}</h2>
      <div className="border-primary-light/30 space-y-4 rounded-md border p-4">
        {customer === undefined ? (
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <dl className="grid w-full gap-2.5 md:grid-cols-2 md:gap-11.5">
            <div className="space-y-2.5">
              {billingAddress?.company && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Company Name", locale)}</dt>
                  <dd className="font-semibold">{billingAddress?.company}</dd>
                </div>
              )}
              {billingAddressMetadata.contact_person && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Contact person", locale)}</dt>
                  <dd className="font-semibold">{billingAddressMetadata.contact_person}</dd>
                </div>
              )}
              {billingAddressMetadata.vat_number && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("VAT Number", locale)}</dt>
                  <dd className="font-semibold">{billingAddressMetadata.vat_number}</dd>
                </div>
              )}
            </div>
            <div className="space-y-2.5">
              {billingAddress?.address_1 && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Billing Address", locale)}</dt>
                  <dd className="font-semibold">{billingAddress?.address_1}</dd>
                </div>
              )}
              {billingAddress?.postal_code && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("ZIP Code", locale)}</dt>
                  <dd className="font-semibold">{billingAddress?.postal_code}</dd>
                </div>
              )}
              {billingAddress?.city && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("City", locale)}</dt>
                  <dd className="font-semibold">{billingAddress?.city}</dd>
                </div>
              )}
            </div>
          </dl>
        )}
      </div>
    </section>
  );
};

export default BillingRecap;
